// Nine Lives '84 - the alley.
//
// The hub screen. From the street Tabs climbs trash cans, the fence and
// two clotheslines to reach windows that open and close at random. A dog
// patrols the street and charges anyone down there; a rival cat swats him
// off a can if he lingers; mice run the clotheslines and knock him off;
// junk thrown from the windows knocks him down. On an open window's sill,
// the action button climbs in.

import { knockCat, type Cat } from './cat';
import {
  CAT_W,
  DOG_H,
  DOG_W,
  DT,
  GRAVITY,
  JUNK_H,
  JUNK_W,
  K_ACTION,
  MOUSE_H,
  MOUSE_W,
  WINDOW_H,
  WINDOW_W,
  WORLD_W,
} from './constants';
import type { DifficultyParams } from './difficulty';
import { overlaps } from './physics';
import type { Rng } from './rng';
import type { Box, GameEvent, Platform } from './types';

export interface AlleyWindow extends Box {
  index: number;
  open: boolean;
  /** Ticks left open. */
  timer: number;
  sill: Platform;
  /** Clementine is at this window; it stays open until Tabs goes in. */
  felicia: boolean;
}

export interface Dog extends Box {
  dir: 1 | -1;
  /** Ticks until the dog comes back on screen; 0 while present. */
  away: number;
  /** True while the dog has Tabs. */
  biting: boolean;
}

export interface Rival extends Box {
  can: Platform;
  linger: number;
}

export interface Mouse extends Box {
  dir: 1 | -1;
  line: Platform;
}

export interface Junk extends Box {
  vx: number;
  vy: number;
}

/** A clothesline runs like a pulley, one way or the other, carrying whoever stands on it. */
export interface Clothesline extends Platform {
  dir: 1 | -1;
}

/** Column x positions and sill heights of the window grid. */
const WINDOW_COLS = [30, 92, 154, 216, 278];
const SILL_TOPS = [110, 168];

export class Alley {
  readonly cans: Platform[];
  readonly fence: Platform;
  readonly lines: Clothesline[];
  readonly windows: AlleyWindow[] = [];
  readonly platforms: Platform[];
  readonly dog: Dog;
  readonly rival: Rival;
  mice: Mouse[] = [];
  junk: Junk[] = [];
  private mouseTimer: number;
  private junkTimer: number;

  constructor(
    private readonly rng: Rng,
    public params: DifficultyParams,
  ) {
    // Five bins along the street, small and large by turns; the large ones
    // are the step up to the fence. The middle of the street (where Tabs
    // starts) and the far left (where he respawns) stay clear.
    this.cans = [
      { kind: 'can', x: 26, y: 0, w: 20, h: 24, oneWay: false },
      { kind: 'can', x: 76, y: 0, w: 24, h: 36, oneWay: false },
      { kind: 'can', x: 126, y: 0, w: 20, h: 24, oneWay: false },
      { kind: 'can', x: 186, y: 0, w: 24, h: 36, oneWay: false },
      { kind: 'can', x: 240, y: 0, w: 20, h: 24, oneWay: false },
    ];
    this.fence = { kind: 'fence', x: 0, y: 66, w: WORLD_W, h: 6, oneWay: true };
    this.lines = [
      { kind: 'line', x: 16, y: 98, w: 288, h: 2, oneWay: true, dir: 1 },
      { kind: 'line', x: 16, y: 134, w: 288, h: 2, oneWay: true, dir: -1 },
    ];
    let index = 0;
    for (const top of SILL_TOPS) {
      for (const x of WINDOW_COLS) {
        const sill: Platform = { kind: 'sill', x, y: top - 3, w: WINDOW_W, h: 3, oneWay: true };
        this.windows.push({
          index: index++,
          x,
          y: top,
          w: WINDOW_W,
          h: WINDOW_H,
          open: false,
          timer: 0,
          sill,
          felicia: false,
        });
      }
    }
    this.platforms = [
      ...this.cans,
      this.fence,
      ...this.lines,
      ...this.windows.map((w) => w.sill),
    ];
    this.dog = { x: -DOG_W - 10, y: 0, w: DOG_W, h: DOG_H, dir: 1, away: 90, biting: false };
    this.rival = { x: 0, y: 0, w: CAT_W, h: 10, can: this.cans[3], linger: 0 };
    this.seatRival();
    this.mouseTimer = rng.int(...params.mouseEvery);
    this.junkTimer = rng.int(...params.junkEvery);
  }

  /** Open windows, not counting Clementine's. */
  get openCount(): number {
    return this.windows.filter((w) => w.open && !w.felicia).length;
  }

  get feliciaWindow(): AlleyWindow | null {
    return this.windows.find((w) => w.felicia) ?? null;
  }

  /** Clementine appears at a random window on the top row and waits there. */
  showFelicia(): AlleyWindow {
    this.hideFelicia();
    const top = this.windows.filter((w) => w.y === Math.max(...this.windows.map((v) => v.y)));
    const w = top[this.rng.int(0, top.length - 1)];
    w.felicia = true;
    w.open = true;
    w.timer = 0;
    return w;
  }

  hideFelicia(): void {
    for (const w of this.windows) {
      if (w.felicia) {
        w.felicia = false;
        w.open = false;
      }
    }
  }

  /** The window whose sill Tabs is standing on, if any. */
  sillUnder(cat: Cat): AlleyWindow | null {
    if (!cat.ground || cat.ground.kind !== 'sill') return null;
    return this.windows.find((w) => w.sill === cat.ground) ?? null;
  }

  /** Force a window's state (tests, room exits). */
  setWindow(index: number, open: boolean, ticks = 600): void {
    const w = this.windows[index];
    w.open = open;
    w.timer = open ? ticks : 0;
  }

  /** Shut every ordinary window; Clementine's stays as it is. */
  closeAll(): void {
    for (const w of this.windows) {
      if (w.felicia) continue;
      w.open = false;
      w.timer = 0;
    }
  }

  /** Send the dog off screen for a while (after a respawn). */
  sendDogAway(): void {
    this.dog.biting = false;
    this.dog.away = this.rng.int(...this.params.dogAway);
    this.dog.x = this.dog.dir > 0 ? WORLD_W + 10 : -DOG_W - 10;
    this.dog.dir = this.dog.dir > 0 ? -1 : 1;
  }

  /**
   * Advance everything but Tabs himself (who has already moved this
   * tick). Returns the window Tabs is climbing into, or null.
   */
  update(cat: Cat, keys: number, events: GameEvent[]): AlleyWindow | null {
    const alive = cat.state === 'alive';
    this.updateWindows(events);
    this.updateDog(cat);
    if (alive) {
      this.carryOnLine(cat);
      this.updateRival(cat, events);
      this.updateMice(cat, events);
      this.updateJunk(cat, events);
      const win = this.sillUnder(cat);
      if (win && win.open && keys & K_ACTION) return win;
    }
    return null;
  }

  /** Does the dog have Tabs this tick? Checked by the game after update. */
  dogCatches(cat: Cat): boolean {
    const d = this.dog;
    if (d.away > 0 || cat.state !== 'alive' || cat.invuln > 0) return false;
    return cat.y < 6 && overlaps(cat, d);
  }

  /** How fast a line is running, signed. */
  drift(line: Clothesline): number {
    return line.dir * this.params.lineDrift;
  }

  /** A clothesline carries Tabs along while he stands on it. */
  private carryOnLine(cat: Cat): void {
    const line = cat.ground as Clothesline | null;
    if (!line || line.kind !== 'line') return;
    cat.x = Math.max(0, Math.min(WORLD_W - cat.w, cat.x + this.drift(line) * DT));
  }

  private updateWindows(events: GameEvent[]): void {
    const p = this.params;
    let open = this.openCount;
    for (const w of this.windows) {
      if (w.felicia) continue;
      if (w.open) {
        if (--w.timer <= 0) {
          w.open = false;
          open--;
        }
      } else if (open < p.maxOpen && this.rng.chance(p.windowOpenChance)) {
        w.open = true;
        w.timer = this.rng.int(...p.windowOpenTicks);
        open++;
        events.push({ kind: 'windowOpen' });
      }
    }
  }

  private updateDog(cat: Cat): void {
    const d = this.dog;
    if (d.biting) return;
    if (d.away > 0) {
      d.away--;
      return;
    }
    const catOnStreet = cat.state === 'alive' && cat.invuln === 0 && cat.y < 6;
    let speed = this.params.dogSpeed;
    if (catOnStreet) {
      d.dir = cat.x + cat.w / 2 > d.x + d.w / 2 ? 1 : -1;
      speed = this.params.dogCharge;
    }
    d.x += d.dir * speed * DT;
    if (catOnStreet) {
      d.x = Math.max(0, Math.min(WORLD_W - d.w, d.x));
    } else if (d.x > WORLD_W + 10 || d.x < -d.w - 10) {
      this.sendDogAway();
    }
  }

  private seatRival(): void {
    const c = this.rival.can;
    this.rival.x = c.x + c.w / 2 - this.rival.w / 2;
    this.rival.y = c.y + c.h;
  }

  private updateRival(cat: Cat, events: GameEvent[]): void {
    const r = this.rival;
    if (cat.ground === r.can) {
      r.linger++;
      if (r.linger > this.params.rivalPatience) {
        const dir = cat.x + cat.w / 2 < r.x + r.w / 2 ? -1 : 1;
        knockCat(cat, dir * 110, 90, 'rival', events);
        r.linger = 0;
        const others = this.cans.filter((c) => c !== r.can);
        r.can = others[this.rng.int(0, others.length - 1)];
        this.seatRival();
      }
    } else if (r.linger > 0) {
      r.linger = Math.max(0, r.linger - 2);
    }
  }

  private updateMice(cat: Cat, events: GameEvent[]): void {
    if (--this.mouseTimer <= 0) {
      this.mouseTimer = this.rng.int(...this.params.mouseEvery);
      const line = this.lines[this.rng.int(0, this.lines.length - 1)];
      const dir: 1 | -1 = this.rng.chance(0.5) ? 1 : -1;
      this.mice.push({
        x: dir > 0 ? line.x - MOUSE_W : line.x + line.w,
        y: line.y + line.h,
        w: MOUSE_W,
        h: MOUSE_H,
        dir,
        line,
      });
    }
    const speed = this.params.mouseSpeed;
    this.mice = this.mice.filter((m) => {
      m.x += m.dir * speed * DT;
      if (cat.ground === m.line && overlaps(cat, m)) {
        knockCat(cat, m.dir * 45, 30, 'mouse', events);
      }
      return m.dir > 0 ? m.x < m.line.x + m.line.w : m.x + m.w > m.line.x;
    });
  }

  private updateJunk(cat: Cat, events: GameEvent[]): void {
    if (--this.junkTimer <= 0) {
      this.junkTimer = this.rng.int(...this.params.junkEvery);
      const open = this.windows.filter((w) => w.open);
      const from = open.length
        ? open[this.rng.int(0, open.length - 1)]
        : this.windows[this.rng.int(0, this.windows.length - 1)];
      this.junk.push({
        x: from.x + from.w / 2 - JUNK_W / 2,
        y: from.y + 4,
        w: JUNK_W,
        h: JUNK_H,
        vx: this.rng.int(-30, 30),
        vy: 25,
      });
    }
    this.junk = this.junk.filter((j) => {
      j.vy -= GRAVITY * DT;
      j.x += j.vx * DT;
      j.y += j.vy * DT;
      if (cat.knockedTicks === 0 && overlaps(cat, j)) {
        knockCat(cat, (j.vx >= 0 ? 1 : -1) * 55, 40, 'junk', events);
        return false;
      }
      return j.y > -20;
    });
  }
}
